import { useCallback, useEffect, useState } from 'react';
import {
  useNavigate,
  useOutletContext,
  useParams,
} from 'react-router-dom';
import { toast } from 'react-toastify';
import { skipToken } from '@reduxjs/toolkit/query';
import ModalMockFillButton from 'components/ModalMockFillButton/ModalMockFillButton';
import AppRoutes from 'constants/AppRoutes';
import { DATA_SETS_ENUM_TYPES, UID_NAME_TUPLES } from 'constants/dataSets';
import { VALUE_CHAIN_LIST, VALUE_CHAIN_SECTION } from 'constants/formFields';
import { VALUE_CHAIN_POSITION_DOWNSTREAM } from 'constants/general';
import useDataSetsDate from 'hooks/useDataSetsDate';
import useFieldsErrors from 'hooks/useFieldsErrors';
import useScrollToError from 'hooks/useScrollToError';
import useValueChainPosition from 'hooks/useValueChainPosition';
import i18n from 'i18n';
import { getCreateProductPageMockValues } from 'mocks/dataSetsCreatePageMocks';
import {
  useEnumQuery,
  useGetProductCategoriesQuery,
  useProductCreateMutation,
  useProductRecordQuery,
  useProductRecordUpdateMutation,
  useUidNameTuplesQuery,
} from 'store/slices/dataSets/apis/dataSetsApi';
import { selectEnums, selectUidNameTuples } from 'store/slices/dataSets/selectors';
import { useUserMeQuery } from 'store/slices/user/apis/user';
import getRequestDataForProduct from 'utils/formHelpers/getRequestDataForProduct';
import { getRelationTypeByValueChain } from 'utils/formHelpers/productTypeValueChain';

import useCreateProductForm from './hooks/useCreateProductForm';
import CreateProductView from './CreateProductView';

import type IDataSetsOutletContext from 'layout/DataSetsLayout/interfaces/IDataSetsOutletContext';
import type { ReactElement } from 'react';
import type IProductRecordRequest from 'store/slices/dataSets/interfaces/IProductRecordRequest';
import type { TCreateProductSchema } from 'utils/validators';

function CreateProduct(): ReactElement {
  const navigate = useNavigate();
  const { recordId = '' } = useParams();
  const { setTitleAction } = useOutletContext<IDataSetsOutletContext>();

  const { data: uidNameTuples, isLoading: isNaceUidNameTuplesLoading } = useUidNameTuplesQuery({
    types: [UID_NAME_TUPLES.profitCenter],
  }, {
    selectFromResult: (result) => ({ ...result, data: selectUidNameTuples(result.data) }),
  });

  const [productCreate, { isLoading: isProductCreateLoading }] = useProductCreateMutation();

  const [productRecordUpdate, { isLoading: isProductRecordUpdateLoading }] = useProductRecordUpdateMutation();
  const { setFieldsErrors } = useFieldsErrors();

  const { data: enumData, isLoading: isEnumLoading } = useEnumQuery({
    types: [DATA_SETS_ENUM_TYPES.currency, DATA_SETS_ENUM_TYPES.volumeQtyUnit,
      DATA_SETS_ENUM_TYPES.relianceOnFreeNaturalResources, DATA_SETS_ENUM_TYPES.relianceOnFreeSocialResources,
      DATA_SETS_ENUM_TYPES.criticalityRating, DATA_SETS_ENUM_TYPES.upstreamTier,
      DATA_SETS_ENUM_TYPES.valueChainSection],
  }, {
    selectFromResult: (result) => ({ ...result, data: selectEnums(result.data) }),
  });

  const { data: productCategories, isLoading: isProductCategoriesLoading } = useGetProductCategoriesQuery();
  const productCategoriesOptions = productCategories?.data?.items.map((category) => ({
    label: category.name,
    value: `${category.id}`,
  }));

  const fetchQuery = recordId ? {
    recordId,
  } : skipToken;
  const { isLoading: isProductRecordLoading } = useProductRecordQuery(fetchQuery);

  const { data: userData, isLoading: isUserMeLoading } = useUserMeQuery();
  const companyCurrency = userData?.data?.company?.currency;

  const isPageLoading = isEnumLoading || isNaceUidNameTuplesLoading || isProductCreateLoading
      || isProductRecordUpdateLoading || isProductRecordLoading || isUserMeLoading || isProductCategoriesLoading;

  const productEndpoint = recordId ? productRecordUpdate : productCreate;
  const {
    control,
    handleSubmit,
    isDirty,
    reset,
    setError,
    fieldsName,
    watch,
    setValue,
    profitCenterIds,
    formState,
  } = useCreateProductForm({ recordId, isPageLoading, productCategoriesOptions });

  const {
    handleGetVcOptions,
    vcOptionsList,
    listPosition,
  } = useValueChainPosition(watch(VALUE_CHAIN_LIST)?.value);

  const vcOptionsListFiltered = vcOptionsList?.filter(
    (vcOption) => vcOption.value !== VALUE_CHAIN_POSITION_DOWNSTREAM,
  );

  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);

  useScrollToError({ formState });

  const onProductSubmit = handleSubmit((values) => {
    const requestData: IProductRecordRequest = getRequestDataForProduct({ values, recordId, companyCurrency });

    productEndpoint(requestData).unwrap()
      .then(
        () => {
          toast.success(i18n.t(
            recordId ? 'notifications.recordHasBeenUpdated'
              : 'notifications.recordHasBeenAdded',
          ));
          reset(undefined, { keepValues: true });
          navigate(AppRoutes.products, { replace: true, flushSync: true });
        },
      ).catch((errors) => {
        setFieldsErrors<TCreateProductSchema>({ errors, setError, fieldsName });
      });
  });

  const onHandleCloseConfirmation = (isOpen = true) => {
    if (!isConfirmationModalOpen && !isDirty) {
      navigate(-1);
      return;
    }

    setIsConfirmationModalOpen(isOpen);
  };

  const onDiscardChangesConfirm = () => {
    reset(undefined, { keepValues: true });
    setIsConfirmationModalOpen(false);
    navigate(-1);
  };

  const {
    dateOpened, startDate, onStartDateChange, onDateOpenedChange,
  } = useDataSetsDate<TCreateProductSchema>({ watch, setValue });

  const isMockFillVisible = !recordId && process.env.REACT_APP_API?.includes('api.dev');

  const onFillMockData = useCallback(() => {
    const valueChainListOption = watch(VALUE_CHAIN_LIST);
    const valueChainSectionOption = watch(VALUE_CHAIN_SECTION);

    const relationType = getRelationTypeByValueChain(valueChainListOption?.value);

    const mockValues = getCreateProductPageMockValues({
      enumData,
      profitCenterOptions: profitCenterIds?.[UID_NAME_TUPLES.profitCenter],
      unitLinkOptions: relationType ? uidNameTuples?.[relationType] : [],
      relationType,
      productCategoriesOptions,
    });

    reset({
      ...watch(),
      ...mockValues,
      [VALUE_CHAIN_LIST]: valueChainListOption?.value ? valueChainListOption : vcOptionsListFiltered?.[0],
      [VALUE_CHAIN_SECTION]: valueChainSectionOption?.value ? valueChainSectionOption : listPosition?.[0],
    });
  }, [
    enumData,
    listPosition,
    productCategoriesOptions,
    profitCenterIds,
    reset,
    uidNameTuples,
    vcOptionsListFiltered,
    watch,
  ]);

  useEffect(() => {
    if (!isMockFillVisible) {
      setTitleAction(null);
      return undefined;
    }

    setTitleAction(
      <ModalMockFillButton
        onClick={onFillMockData}
        disabled={isPageLoading}
      />,
    );

    return () => {
      setTitleAction(null);
    };
  }, [isMockFillVisible, isPageLoading, onFillMockData, setTitleAction]);

  return (
    <CreateProductView
      control={control}
      onProductSubmit={onProductSubmit}
      onHandleCloseConfirmation={onHandleCloseConfirmation}
      isConfirmationModalOpen={isConfirmationModalOpen}
      onDiscardChangesConfirm={onDiscardChangesConfirm}
      enumData={enumData}
      uidNameTuples={uidNameTuples}
      isPageLoading={isPageLoading}
      dateOpened={dateOpened}
      startDate={startDate}
      onDateOpenedChange={onDateOpenedChange}
      onStartDateChange={onStartDateChange}
      profitCenterIds={profitCenterIds}
      watch={watch}
      setValue={(field: string, value: unknown) => {
        setValue(field as keyof TCreateProductSchema, value as TCreateProductSchema[keyof TCreateProductSchema]);
      }}
      handleGetVcOptions={handleGetVcOptions}
      vcOptionsList={vcOptionsListFiltered}
      listPosition={listPosition}
      productCategoriesOptions={productCategoriesOptions}
    />
  );
}

export default CreateProduct;
